import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useAppSelector } from "../../app/store/configureStore";
import { ResourceCollectionIIAnswersProps } from "../../app/models/Answers";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import RCIIFormValidationSchema from "../../app/validations/RCIIFormValidation";
import { useEffect, useState } from "react";

const ResourceCollectionIIForm: React.FC<ResourceCollectionIIAnswersProps> = ({
  answersData,
  onAnswerChange,
  onSave,
  ResourceCollectionIICodes,
}) => {
  const { user } = useAppSelector((state) => state.account);
  const [ageOption, setAgeOption] = useState<string>();
  const [ageOption2, setAgeOption2] = useState<string>();
  const [ageOption3, setAgeOption3] = useState<string>();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  const defaultValues = {
    title: answersData.find(
      (answer) => answer.systemCodeId === ResourceCollectionIICodes.titleCode
    )?.answer,
    titleBilingual: answersData.find(
      (answer) =>
        answer.systemCodeId === ResourceCollectionIICodes.titleBilingualCode
    )?.answer,
    ageGroup: answersData.find(
      (answer) => answer.systemCodeId === ResourceCollectionIICodes.ageGroupCode
    )?.answer,
    ageGroupBiligual: answersData.find(
      (answer) =>
        answer.systemCodeId === ResourceCollectionIICodes.ageGroupBiligualCode
    )?.answer,
    intendedGoals: answersData.find(
      (answer) =>
        answer.systemCodeId === ResourceCollectionIICodes.intendedGoalsCode
    )?.answer,
    intendedGoalsBiligual: answersData.find(
      (answer) =>
        answer.systemCodeId ===
        ResourceCollectionIICodes.intendedGoalsBiligualCode
    )?.answer,
    materials: answersData.find(
      (answer) =>
        answer.systemCodeId === ResourceCollectionIICodes.materialsCode
    )?.answer,
    materialsBiligual: answersData.find(
      (answer) =>
        answer.systemCodeId === ResourceCollectionIICodes.materialsBiligualCode
    )?.answer,
    process: answersData.find(
      (answer) => answer.systemCodeId === ResourceCollectionIICodes.processCode
    )?.answer,
    processBiligual: answersData.find(
      (answer) =>
        answer.systemCodeId === ResourceCollectionIICodes.processBiligualCode
    )?.answer,
    development: answersData.find(
      (answer) =>
        answer.systemCodeId === ResourceCollectionIICodes.developmentCode
    )?.answer,
    developmentBiligual: answersData.find(
      (answer) =>
        answer.systemCodeId ===
        ResourceCollectionIICodes.developmentBiligualCode
    )?.answer,
  };

  const validationSchema = RCIIFormValidationSchema({
    languageSpecialization: !!user?.languageSpecialization,
  });

  const {
    register,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
  });

  useEffect(() => {
    if (user?.credentialTypeId === 4) {
      setAgeOption("Families of Infants");
      setAgeOption2("Families of Toddlers");
      setAgeOption3("Families of 3-yr-olds");
    }
    if (user?.credentialTypeId === 3) {
      setAgeOption("Infants");
      setAgeOption2("Toddlers");
      setAgeOption3("Preschoolers");
    }
    if (user?.credentialTypeId === 2) {
      setAgeOption("Three-year-olds");
      setAgeOption2("Four-year-olds");
      setAgeOption3("Five-year-olds");
    }
    if (user?.credentialTypeId === 1) {
      setAgeOption("Young Infants");
      setAgeOption2("Mobile Infants");
      setAgeOption3("Toddlers");
    }
  }, [user]);

  return (
    <Grid item>
      <Grid container spacing={3}>
        <Grid
          item
          container
          spacing={4}
          xs={12}
          md={user?.languageSpecialization ? 6 : 12}
        >
          <Grid item xs={isMobile || user?.languageSpecialization ? 12 : 6}>
            <TextField
              {...register("title")}
              label={
                user?.credentialTypeId === 4
                  ? "Title"
                  : "Learning Experience Title"
              }
              value={
                answersData.find(
                  (answer) =>
                    answer.systemCodeId === ResourceCollectionIICodes.titleCode
                )?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = ResourceCollectionIICodes.titleCode;
                onAnswerChange(systemCodeId, newValue);
              }}
              required
              fullWidth
              error={!!errors.title?.message}
              helperText={errors.title?.message}
            />
          </Grid>
          <Grid item xs={isMobile || user?.languageSpecialization ? 12 : 6}>
            <FormControl required fullWidth error={!!errors.ageGroup?.message}>
              <InputLabel>Age Group</InputLabel>
              <Select
                {...register("ageGroup")}
                label="Age group"
                value={
                  answersData.find(
                    (answer) =>
                      answer.systemCodeId ===
                      ResourceCollectionIICodes.ageGroupCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId = ResourceCollectionIICodes.ageGroupCode;

                  onAnswerChange(systemCodeId, newValue);
                }}
              >
                <MenuItem value={ageOption}>
                  {user?.credentialTypeId === 4 && "Families of Infants"}
                  {user?.credentialTypeId === 3 && "Infants"}
                  {user?.credentialTypeId === 2 && "Three-year-olds"}
                  {user?.credentialTypeId === 1 && "Young Infants"}
                </MenuItem>
                <MenuItem value={ageOption2}>
                  {user?.credentialTypeId === 4 && "Families of Toddlers"}
                  {user?.credentialTypeId === 3 && "Toddlers"}
                  {user?.credentialTypeId === 2 && "Four-year-olds"}
                  {user?.credentialTypeId === 1 && "Mobile Infants"}
                </MenuItem>
                <MenuItem value={ageOption3}>
                  {user?.credentialTypeId === 4 && "Families of 3-yr-olds"}
                  {user?.credentialTypeId === 3 && "Preschoolers"}
                  {user?.credentialTypeId === 2 && "Five-year-olds"}
                  {user?.credentialTypeId === 1 && "Toddlers"}
                </MenuItem>
                {user?.credentialTypeId === 4 && (
                  <MenuItem value={"Families of 4- and 5-yr-olds"}>
                    "Families of 4- and 5-yr-olds"
                  </MenuItem>
                )}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register("intendedGoals")}
              label={
                user?.credentialTypeId === 4
                  ? "Intended Goals"
                  : "Intended Goals -  what children will experience or learn:  "
              }
              value={
                answersData.find(
                  (answer) =>
                    answer.systemCodeId ===
                    ResourceCollectionIICodes.intendedGoalsCode
                )?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId =
                  ResourceCollectionIICodes.intendedGoalsCode;
                newValue.length <= 1000 &&
                  onAnswerChange(systemCodeId, newValue);
              }}
              multiline
              rows={4}
              required
              fullWidth
              error={!!errors.intendedGoals?.message}
              helperText={errors.intendedGoals?.message}
            />
            <Typography fontSize={13}>
              {defaultValues.intendedGoals?.length}/1000
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register("materials")}
              label={
                user?.credentialTypeId === 4
                  ? "Materials:"
                  : "Materials - what you need to complete the activity:  "
              }
              value={
                answersData.find(
                  (answer) =>
                    answer.systemCodeId ===
                    ResourceCollectionIICodes.materialsCode
                )?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = ResourceCollectionIICodes.materialsCode;
                newValue.length <= 1000 &&
                  onAnswerChange(systemCodeId, newValue);
              }}
              multiline
              rows={4}
              required
              fullWidth
              error={!!errors.materials?.message}
              helperText={errors.materials?.message}
            />
            <Typography fontSize={13}>
              {defaultValues.materials?.length}/1000
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <TextField
              {...register("process")}
              label={
                user?.credentialTypeId === 4
                  ? "Process/Teaching Strategies:"
                  : "Process/Teaching strategies - how you will complete the activity:"
              }
              value={
                answersData.find(
                  (answer) =>
                    answer.systemCodeId ===
                    ResourceCollectionIICodes.processCode
                )?.answer || ""
              }
              onChange={(e) => {
                const newValue = e.target.value;
                const systemCodeId = ResourceCollectionIICodes.processCode;
                newValue.length <= 1000 &&
                  onAnswerChange(systemCodeId, newValue);
              }}
              multiline
              rows={4}
              required
              fullWidth
              error={!!errors.process?.message}
              helperText={errors.process?.message}
            />
            <Typography fontSize={13}>
              {defaultValues.process?.length}/1000
            </Typography>
          </Grid>
          {user?.credentialTypeId !== 4 && (
            <Grid item xs={12}>
              <TextField
                {...register("development")}
                label="Developmentally Appropriate - how is this experience developmentally appropriate:"
                value={
                  answersData.find(
                    (answer) =>
                      answer.systemCodeId ===
                      ResourceCollectionIICodes.developmentCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId =
                    ResourceCollectionIICodes.developmentCode;
                  newValue.length <= 1000 &&
                    onAnswerChange(systemCodeId, newValue);
                }}
                multiline
                rows={4}
                required
                fullWidth
                error={!!errors.development?.message}
                helperText={errors.development?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.development?.length}/1000
              </Typography>
            </Grid>
          )}
        </Grid>

        {user?.languageSpecialization && (
          <Grid item container spacing={4} xs={12} md={6}>
            <Grid item xs={12}>
              <TextField
                {...register("titleBilingual")}
                label="Title (in your second language)"
                value={
                  answersData.find(
                    (answer) =>
                      answer.systemCodeId ===
                      ResourceCollectionIICodes.titleBilingualCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId =
                    ResourceCollectionIICodes.titleBilingualCode;
                  onAnswerChange(systemCodeId, newValue);
                }}
                required
                fullWidth
                error={!!errors.titleBilingual?.message}
                helperText={errors.titleBilingual?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("ageGroupBiligual")}
                label="Age Group (in your second language)"
                value={
                  answersData.find(
                    (answer) =>
                      answer.systemCodeId ===
                      ResourceCollectionIICodes.ageGroupBiligualCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId =
                    ResourceCollectionIICodes.ageGroupBiligualCode;
                  onAnswerChange(systemCodeId, newValue);
                }}
                required
                fullWidth
                error={!!errors.ageGroupBiligual?.message}
                helperText={errors.ageGroupBiligual?.message}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("intendedGoalsBiligual")}
                label={
                  user?.credentialTypeId === 4
                    ? "Intended Goals (in your second language)"
                    : "Intended Goals -  what children will experience or learn:(in your second language)"
                }
                value={
                  answersData.find(
                    (answer) =>
                      answer.systemCodeId ===
                      ResourceCollectionIICodes.intendedGoalsBiligualCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId =
                    ResourceCollectionIICodes.intendedGoalsBiligualCode;
                  newValue.length <= 1000 &&
                    onAnswerChange(systemCodeId, newValue);
                }}
                multiline
                rows={4}
                required
                fullWidth
                error={!!errors.intendedGoalsBiligual?.message}
                helperText={errors.intendedGoalsBiligual?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.intendedGoalsBiligual?.length}/1000
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("materialsBiligual")}
                label={
                  user?.credentialTypeId === 4
                    ? "Materials:"
                    : "Materials - what you need to complete the activity:(in your second language)"
                }
                value={
                  answersData.find(
                    (answer) =>
                      answer.systemCodeId ===
                      ResourceCollectionIICodes.materialsBiligualCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId =
                    ResourceCollectionIICodes.materialsBiligualCode;
                  newValue.length <= 1000 &&
                    onAnswerChange(systemCodeId, newValue);
                }}
                multiline
                rows={4}
                required
                fullWidth
                error={!!errors.materialsBiligual?.message}
                helperText={errors.materialsBiligual?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.materialsBiligual?.length}/1000
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <TextField
                {...register("processBiligual")}
                label={
                  user?.credentialTypeId === 4
                    ? "Process/Teaching Strategies:(in your second language)"
                    : "Process/Teaching strategies - how you will complete the activity:(in your second language)"
                }
                value={
                  answersData.find(
                    (answer) =>
                      answer.systemCodeId ===
                      ResourceCollectionIICodes.processBiligualCode
                  )?.answer || ""
                }
                onChange={(e) => {
                  const newValue = e.target.value;
                  const systemCodeId =
                    ResourceCollectionIICodes.processBiligualCode;
                  newValue.length <= 1000 &&
                    onAnswerChange(systemCodeId, newValue);
                }}
                multiline
                rows={4}
                required
                fullWidth
                error={!!errors.processBiligual?.message}
                helperText={errors.processBiligual?.message}
              />
              <Typography fontSize={13}>
                {defaultValues.processBiligual?.length}/1000
              </Typography>
            </Grid>
            {user?.credentialTypeId !== 4 && (
              <Grid item xs={12}>
                <TextField
                  {...register("developmentBiligual")}
                  label="Developmentally Appropriate - how this experience is developmentally appropriate:(in your second language)"
                  value={
                    answersData.find(
                      (answer) =>
                        answer.systemCodeId ===
                        ResourceCollectionIICodes.developmentBiligualCode
                    )?.answer || ""
                  }
                  onChange={(e) => {
                    const newValue = e.target.value;
                    const systemCodeId =
                      ResourceCollectionIICodes.developmentBiligualCode;
                    newValue.length <= 1000 &&
                      onAnswerChange(systemCodeId, newValue);
                  }}
                  multiline
                  rows={4}
                  required
                  fullWidth
                  error={!!errors.developmentBiligual?.message}
                  helperText={errors.developmentBiligual?.message}
                />
                <Typography fontSize={13}>
                  {defaultValues.developmentBiligual?.length}/1000
                </Typography>
              </Grid>
            )}
          </Grid>
        )}
        <Grid item xs={12} mr={2}>
          <Button
            onClick={onSave}
            color="info"
            variant="outlined"
            sx={{ marginRight: 3, marginBottom: isMobile ? 2 : 0 }}
          >
            Save
          </Button>
        </Grid>
      </Grid>
    </Grid>
  );
};
export default ResourceCollectionIIForm;
